/**
 * Undo — pure derivation.
 *
 * How to put a move BACK: the request that restores each moved assignment,
 * and the plan the toast's Undo button carries. Nothing here touches React
 * or the server.
 */
import { MoveRequest, RosterAssignment } from "../../../../../services/roster-assignments-api";
import { orderIdOf } from "../../../utils/orders";

export type MoveTarget =
	| { kind: "worker"; workerId: string; workerName: string }
	| { kind: "phase"; workOrderId: number; phaseId: number; label?: string };

/**
 * One undo per assignment that moved: back to the worker it had (a move to
 * another person) or back to the step + phase it was on (a move to another
 * line). The restore is forced through — the assignment WAS there a moment
 * ago, so a qualification or overtime question has already been answered.
 *
 * A block that had no phase (an "Other" card) cannot be sent back by phase —
 * the server would reject `targetPhaseId: undefined` — so it goes back by
 * worker, which the server resolves to the same step and clock.
 */
export function buildUndoPayload(previous: RosterAssignment, movedTo: MoveTarget): MoveRequest {
	if (movedTo.kind === "worker" || previous.laborStandardPhaseId == null) {
		return {
			targetWorkerId: String(previous.workerId),
			targetWorkerName: previous.workerName,
			allowUnqualified: true,
			allowOvertime: true,
		};
	}
	return {
		targetWorkOrderId: orderIdOf(previous),
		targetPhaseId: previous.laborStandardPhaseId,
		allowUnqualified: true,
		allowOvertime: true,
	};
}

/** Everything an undo needs, captured BEFORE the refetch replaces the rows. */
export interface UndoPlan {
	locationId: number;
	/** assignment id -> its undo request */
	moves: Array<{ id: number; request: MoveRequest }>;
	/** Ids that were not pinned before the move, to unpin once restored. */
	unpinIds: number[];
	/**
	 * Moved ids the loaded week never had (a whole-step move that reached into
	 * another week). Nothing is known about where they were, so they cannot be
	 * put back from here — the toast says so instead of staying silent.
	 */
	missingIds: number[];
	/**
	 * Pieces that never moved but were pinned by the change (a lend's before
	 * and after parts); unpinned once every move is back.
	 */
	alsoUnpinIds?: number[];
}

export function planUndo(
	movedIds: number[],
	before: RosterAssignment[],
	movedTo: MoveTarget,
	locationId: number
): UndoPlan {
	const byId = new Map(before.map((a) => [a.id, a]));
	const moves: UndoPlan["moves"] = [];
	const unpinIds: number[] = [];
	const missingIds: number[] = [];
	movedIds.forEach((id) => {
		const previous = byId.get(id);
		if (!previous) {
			missingIds.push(id);
			return;
		}
		moves.push({ id, request: buildUndoPayload(previous, movedTo) });
		if (!previous.isManuallyAssigned) {
			unpinIds.push(id);
		}
	});
	return { locationId, moves, unpinIds, missingIds };
}

/**
 * Undo for a lend ("for a while, then back"): the lent piece(s) go back to
 * the block's own step and phase; the parts that stayed are left where they
 * are. When the block was not pinned before, every piece loses its pin again
 * so the next fill treats it as allocated.
 */
export function planLendUndo(
	original: RosterAssignment,
	movedIds: number[],
	stayedIds: number[],
	locationId: number
): UndoPlan {
	const back = buildUndoPayload(original, { kind: "phase", workOrderId: orderIdOf(original) ?? original.workOrderId, phaseId: original.laborStandardPhaseId ?? 0 });
	return {
		locationId,
		moves: movedIds.map((id) => ({ id, request: back })),
		unpinIds: original.isManuallyAssigned ? [] : [...movedIds],
		missingIds: [],
		alsoUnpinIds: original.isManuallyAssigned ? [] : stayedIds.filter((id) => id > 0),
	};
}
